import { useCallback, useEffect, useRef, useState } from "react";

const HIGHLIGHT_CLEAR_TIMEOUT_MS = 2000;

export interface HighlightTargetController {
  /** Currently highlighted message ID, or null. */
  highlightedMessageId: string | null;
  /** Set the highlighted message ID directly (e.g. to clear on channel switch). */
  setHighlightedMessageId: (id: string | null) => void;
  /**
   * Schedule the highlight to clear after the timeout. Called once a
   * scroll-to-target completes successfully — the message flashes for
   * 2s then fades back to the normal background.
   */
  scheduleHighlightClear: () => void;
  /** Imperative cancel — used by channel-switch reset. */
  clearTimer: () => void;
}

/**
 * Owns the `highlightedMessageId` state and its 2-second auto-clear
 * timer. Exposed as a controller so the surrounding scroll-target
 * state machine can both write the ID and reset the pending clear on
 * channel switch.
 */
export function useHighlightTarget(): HighlightTargetController {
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const clearHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const scheduleHighlightClear = useCallback(() => {
    if (clearHighlightTimerRef.current) {
      clearTimeout(clearHighlightTimerRef.current);
    }
    clearHighlightTimerRef.current = setTimeout(() => {
      clearHighlightTimerRef.current = null;
      setHighlightedMessageId(null);
    }, HIGHLIGHT_CLEAR_TIMEOUT_MS);
  }, []);

  const clearTimer = useCallback(() => {
    if (clearHighlightTimerRef.current) {
      clearTimeout(clearHighlightTimerRef.current);
      clearHighlightTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (clearHighlightTimerRef.current) {
        clearTimeout(clearHighlightTimerRef.current);
      }
    };
  }, []);

  return {
    highlightedMessageId,
    setHighlightedMessageId,
    scheduleHighlightClear,
    clearTimer,
  };
}
